/**
 * TODO Include short week days instead of S M T W etc, Sun, Mon, Tue, Wed.
 * https://stackoverflow.com/questions/64055877/angular-material-how-to-format-datepicker-day-label - except without using moment.
 */
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IAvailabilityGetData } from '@core/availability/availability-get-data.model';
import { AvailabilityService } from '@core/availability/availability.service';
import { ICalendarAvailability } from '@core/availability/calendar-availability.model';
import { ComponentBase } from '@core/base/base.component.base';
import { CalendarAvailabilityComponent } from '@core/calendar-availability/calendar-availability.component';
import { CalendarEventService } from '@core/calendar-availability/calendar-event.service';
import { DateFormatService } from '@core/date/date-format.service';
import { Observable } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { IWhenAttending } from 'src/app/when-attending/when-attending.model';
import { DialogMessageData } from '../dialog.model';
import { IDialogAvailabilityData } from './dialog-availability-data.model';

@Component({
  selector: 'app-dialog-availability',
  templateUrl: './dialog-availability.component.html',
  styleUrls: ['./dialog-availability.component.scss']
})
export class DialogAvailabilityComponent extends ComponentBase implements OnInit {
  selectedDate: IWhenAttending | undefined;
  isDataVisible = false;
  currentDate: Date = new Date();
  activeDate: Date = new Date();
  todaysDate: Date = new Date();
  isMobile = true;
  productId: string | undefined;
  isAvailabilityVisible = true;
  availabilityData: ICalendarAvailability[] = [];
  isFocusEnabled = false;
  isMonthButtonMoved = false;
  isCallToBookEnabled = false;

  @ViewChild('appCalendar', { static: false }) appCalendar!: CalendarAvailabilityComponent;
  constructor(
    public dialogRef: MatDialogRef<DialogAvailabilityComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogMessageData<IDialogAvailabilityData>,
    private dateFormatService: DateFormatService,
    private availabilityService: AvailabilityService,
    private calendarEventService: CalendarEventService
  ) {
    super();
  }

  ngOnInit(): void {
    this.calendarEventService.setCloseDialog(false);
    if (this.data.data) {
      this.setInitialValues(this.data.data);
    }

    this.listenMonthChangeUserEvents().subscribe();
    this.listenCalendarEventCloseDialog().subscribe();
  }

  /** Listens for the calendar event close originating with the calendar header on desktop. */
  listenCalendarEventCloseDialog(): Observable<boolean> {
    return this.calendarEventService.getCloseDialog().pipe(
      filter((value: boolean) => value),
      tap(() => this.closeDialog()),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /** Listens to the calendar event service for the month change event from the calendar-header. */
  listenMonthChangeUserEvents(): Observable<boolean> {
    return this.calendarEventService.getIsMonthClicked().pipe(
      tap((isClick: boolean) => {
        this.isMonthButtonMoved = true;
        this.isFocusEnabled = !isClick;
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /** On init or on month change subscribes to the api. */
  getAvailabilityData(date: Date): Observable<ICalendarAvailability[]> {
    this.availabilityData = [];
    const options: IAvailabilityGetData = {
      isShort: false,
      date,
      productId: this.productId
    };
    return this.availabilityService.getAvailabilityData(options).pipe(
      tap((result: ICalendarAvailability[]) => this.processAvailabilityData(result)),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /** On return of the availability API sets local variables and focus states. */
  private processAvailabilityData(result: ICalendarAvailability[]): void {
    this.availabilityData = result;

    this.appCalendar.availabilityCalendar?.updateTodaysDate();
    if (this.isFocusEnabled && !this.isMonthButtonMoved) {
      this.appCalendar.availabilityCalendar?.focusActiveCell();
    }

    // reset the month moved button.
    this.isMonthButtonMoved = false;
  }

  /** When there is a state change in the calendar checks if it is a month change. */
  checkForMonthChange(activeDate: Date): void {
    if (!this.dateFormatService.isSameMonth(this.activeDate, activeDate)) {
      this.activeDate = activeDate;
      this.getAvailabilityData(activeDate).subscribe();
    }
  }

  /** Sets initial values for the dialog based on the incoming MatDialogRef Data. */
  setInitialValues(data: IDialogAvailabilityData): void {
    this.selectedDate = data.selectedDate;
    this.currentDate = this.selectedDate?.date || this.todaysDate;
    this.activeDate = this.currentDate;
    this.isDataVisible = data.isDataVisible;
    this.productId = data.productId;
    this.isAvailabilityVisible = data.isAvailabilityVisible;
    this.isMobile = this.data.isMobile === undefined ? true : this.data.isMobile;
    this.isCallToBookEnabled = data.isCallToBookEnabled;
    this.getAvailabilityData(this.currentDate).subscribe();
  }

  /** Any key down event in the DOM sets focus Enabled to ensure that focus is maintained between month state changes. */
  onKeyDown(): void {
    this.isFocusEnabled = true;
  }

  onDateSelected(ev: Date): void {
    this.currentDate = ev;
    this.dialogRef.close(this.currentDate);
  }

  /** Closes Dialog triggered from close button in DOM. */
  closeDialog(): void {
    this.dialogRef.close();
  }
}
